import DialogTitle from '@material-ui/core/DialogTitle'
import Button from 'lib/ui/Button'
import Dialog, {DialogBody} from 'lib/ui/Dialog'
import {DarkThemeProvider} from 'lib/ui/theme/ThemeProvider'
import React, {useState} from 'react'
import styled from 'styled-components'
import Icon from 'lib/ui/Icon'
import {Tiny} from 'lib/ui/typography'
import {createButtonBlock} from 'Event/Marketplace/Block/Button/create-button-block'
import {createFaqListBlock} from 'Event/Marketplace/Block/FaqList/create-faq-list-block'
import {createPurchaseFormBlock} from 'Event/Marketplace/Block/PurchaseForm/create-purchase-form-block'
import {createTitleBlock} from 'Event/Marketplace/Block/Title/create-title-block'
import {createTextBlock} from 'Event/Marketplace/Block/Text/create-text-block'
import {createImageBlock} from 'Event/Marketplace/Block/Image/create-image-block'
import {createVideoBlock} from 'Event/Marketplace/Block/Video/create-video-block'
import {createTicketSelectorBlock} from 'Event/Marketplace/Block/TicketSelector/create-ticket-selector-block'
import {createBulletedListBlock} from 'Event/Marketplace/Block/BulletedList/create-bulleted-list.block'
import {createSeparatorBlock} from 'Event/Marketplace/Block/Separator/create-separator-block'
import {createCountdownTimerBlock} from 'Event/Marketplace/Block/CountdownTimer/create-countdown-timer-block'
import {createNumberedListBlock} from 'Event/Marketplace/Block/NumberedList/create-numbered-list-block'
import {createIconBlock} from 'Event/Marketplace/Block/Icon/create-icon-block'
import {createSpacerBlock} from 'Event/Marketplace/Block/Spacer/create-spacer-block'
import {createUpsellPurchaseButtonBlock} from 'Event/Marketplace/Block/UpsellPurchaseButton/create-upsell-purchase-button-block'
import {createUpsellDeclineButtonBlock} from 'Event/Marketplace/Block/UpsellDeclineButton/create-upsell-decline-button'
import {createUpsellDeclineTextBlock} from 'Event/Marketplace/Block/UpsellDeclineText/create-upsell-decline-text'
import {NLPurchasePageBlock} from 'Event/Marketplace/nl-purchase-page'
import {generateHashId} from 'lib/crypto/hash'
import {REMOVE} from 'lib/JsonUpdateProvider'
import {createSectionBlock} from 'Event/Marketplace/Block/NLSectionBlock/create-section-block'
import {DeepPartialSubstitute} from 'lib/type-utils'
import {NLSectionBlock} from 'Event/Marketplace/Block/NLSectionBlock'
import {createPositions} from 'lib/list'

interface AddBlockDialogProps {
  open: boolean
  onClose: () => void
  availableBlocks: Array<NLPurchasePageBlock['type']>
  onAdd: (data: DeepPartialSubstitute<NLSectionBlock, typeof REMOVE>) => void
  insertTargetBlockId: string | null
  blockIds: string[]
}

type BlockDefinitions = {
  [T in NLPurchasePageBlock['type']]: {
    label?: string
    icon: string
  }
}

const blockDefinitions: BlockDefinitions = {
  Title: {
    icon: 'fa-solid fa-book',
  },
  Text: {
    icon: 'fa-regular fa-align-justify',
  },
  Image: {
    icon: 'fa-solid fa-image',
  },
  Video: {
    icon: 'fa-solid fa-film',
  },
  Button: {
    icon: 'fa-solid fa-computer-mouse',
  },
  TicketSelector: {
    label: 'Ticket Selector',
    icon: 'fa-solid fa-ticket',
  },
  PurchaseForm: {
    label: 'Purchase Form',
    icon: 'fa-solid fa-credit-card',
  },
  BulletedList: {
    label: 'Bulleted List',
    icon: 'fa-solid fa-list',
  },
  CountdownTimer: {
    label: 'Countdown Timer',
    icon: 'fa-solid fa-timer',
  },
  NumberedList: {
    label: 'Numbered List',
    icon: 'fa-solid fa-list-ol',
  },
  FaqList: {
    label: 'Faq List',
    icon: 'fa-solid fa-square-question',
  },
  Icon: {
    label: 'Icon',
    icon: 'fa-solid fa-icons',
  },
  Separator: {
    icon: 'fa-solid fa-ruler-horizontal',
  },
  Spacer: {
    label: 'Spacer',
    icon: 'fa-solid fa-square-dashed',
  },
  Section: {
    icon: 'fa-solid fa-grid',
    label: 'Section',
  },
  UpsellPurchaseButton: {
    icon: 'fa-solid fa-shopping-cart',
    label: 'Purchase',
  },
  UpsellDeclineButton: {
    icon: 'fa-solid fa-ban',
    label: 'Decline Button',
  },
  UpsellDeclineText: {
    icon: 'fa-solid fa-ban',
    label: 'Decline Text',
  },
} as const

export const blockDefaultWidth = 12
export const blockDefaultHeight = 8
export const blockDefaultMobileWidth = 2
export const blockDefaultMobileHeight = 10
export const purchaseFormBlockDefaultHeight = 40

export default function AddBlockDialog(props: AddBlockDialogProps) {
  const {
    open,
    onClose,
    availableBlocks,
    onAdd,
    insertTargetBlockId,
    blockIds,
  } = props
  const [block, setBlock] = useState<NLPurchasePageBlock['type'] | null>(null)

  const addBlock = () => {
    if (!block) {
      return
    }

    const createBlock = createBlockHandlers[block]
    const newBlock = createBlock()
    generateHashId([
      block,
      new Date().valueOf().toString(),
      Math.random().toString(),
    ]).then((id) => {
      const targetIndex = blockIds.indexOf(insertTargetBlockId ?? '')
      if (targetIndex === -1) {
        onAdd({
          blocks: {
            [id]: newBlock,
          },
        })
        return
      }

      const moved = Array.from(blockIds)

      moved.splice(targetIndex, 0, id)

      const blocks = createPositions(moved)
      blocks[id] = {
        ...newBlock,
        position: targetIndex + 2,
      }

      onAdd({
        blocks,
      })
    })
    onClose()
  }

  return (
    <DarkThemeProvider>
      <Dialog open={open} onClose={onClose} expandable disableEnforceFocus>
        <DialogTitle>Add Block</DialogTitle>
        <StyledDialogBody>
          <Blocks>
            {availableBlocks.map((type) => {
              const blockType = type as NLPurchasePageBlock['type']
              const definition = blockDefinitions[blockType]

              return (
                <Block
                  key={type}
                  type={type}
                  label={definition.label}
                  icon={definition.icon}
                  selected={block === type}
                  onClick={() => setBlock(blockType)}
                  onDoubleClick={() => {
                    setBlock(blockType)
                    addBlock()
                  }}
                />
              )
            })}
          </Blocks>
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={addBlock}
            disabled={!block}
          >
            Add
          </Button>
        </StyledDialogBody>
      </Dialog>
    </DarkThemeProvider>
  )
}

function Block(props: {
  type: string
  label?: string
  icon: string
  selected: boolean
  onClick: () => void
  onDoubleClick: () => void
}) {
  const {label, type, icon, selected, onClick, onDoubleClick} = props

  return (
    <Container
      selected={selected}
      onClick={onClick}
      onDoubleClick={onDoubleClick}
    >
      <Icon className={icon} iconSize={24} />
      <Tiny color="white" align="center">
        {label ?? type}
      </Tiny>
    </Container>
  )
}

type CreateBlockHandlers = {
  [T in NLPurchasePageBlock as T['type']]: () => NLPurchasePageBlock
}

const createBlockHandlers: CreateBlockHandlers = {
  Title: createTitleBlock,
  Text: createTextBlock,
  Image: createImageBlock,
  Button: createButtonBlock,
  TicketSelector: createTicketSelectorBlock,
  Video: createVideoBlock,
  PurchaseForm: createPurchaseFormBlock,
  Separator: createSeparatorBlock,
  BulletedList: createBulletedListBlock,
  CountdownTimer: createCountdownTimerBlock,
  NumberedList: createNumberedListBlock,
  Icon: createIconBlock,
  FaqList: createFaqListBlock,
  Spacer: createSpacerBlock,
  UpsellPurchaseButton: createUpsellPurchaseButtonBlock,
  UpsellDeclineButton: createUpsellDeclineButtonBlock,
  UpsellDeclineText: createUpsellDeclineTextBlock,
  Section: createSectionBlock,
}

const StyledDialogBody = styled(DialogBody)`
  padding-bottom: ${(props) => props.theme.spacing[8]}!important;
`

const Blocks = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr 1fr 1fr;
  grid-gap: ${(props) => props.theme.spacing[4]};
  padding-bottom: ${(props) => props.theme.spacing[8]};
`

const Container = styled.div<{selected: boolean}>`
  aspect-ratio: 1 / 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${(props) => props.theme.spacing[2]};
  cursor: pointer;
  border-style: solid;
  border-width: ${(props) => (props.selected ? '4px' : '2px')};
  border-color: ${(props) =>
    props.selected ? props.theme.colors.primary : 'white'};
  border-radius: ${(props) => props.theme.spacing[2]};
  i,
  span {
    color: ${(props) =>
      props.selected ? props.theme.colors.primary : 'white'};
  }
  &:hover {
    border-color: ${(props) => props.theme.colors.primary};
    i,
    span {
      color: ${(props) => props.theme.colors.primary};
    }
  }
`
